<template>
    <BulletCard
                    v-auto-animate
                    class="d-flex flex-column"
                    :card-title="reservation.entry_summary?.identification_id || reservation.uid"
                    title-font-size="20px"
                    :bullets="[]"
                >
        <template #title>
            <v-tooltip v-if="entrySearchUrl" location="bottom">
                <template #activator="{ props: entryLinkTooltipProps }">
                    <router-link
                        v-bind="entryLinkTooltipProps"
                        :to="entrySearchUrl"
                        class="entry-id-link"
                        @click.stop
                    >
                        {{ reservation.entry_summary?.identification_id || reservation.uid }}
                    </router-link>
                </template>
                {{ $t("reservations.viewOriginalEntry") }}
            </v-tooltip>
            <template v-if="entrySearchUrl && transportOrderNumber">
                <span class="toolbar-item-separator"></span>
                <span class="text-yellow">{{ transportOrderNumber }}</span>
            </template>
            <template v-if="!entrySearchUrl && transportOrderNumber">
                <span >{{ reservation.entry_summary?.identification_id || reservation.uid }}</span>
                <span class="toolbar-item-separator"></span>
                <span class="text-yellow">{{ transportOrderNumber }}</span>
            </template >
           
        </template>
        <template #status>
            <StatusBadge :status="reservation.status" :tooltip="awaitingConfirmationTooltip" />
        </template>
        <template #default>
        <div class="reservation-card-content py-3" style="overflow-wrap: break-word">
            <div v-auto-animate>
            <v-row align="stretch" density="compact" no-gutters>
                <v-col cols="12" class="reservation-card-main d-flex flex-column">
            <div
                v-if="reservation.entry_summary && (loadingRouteLines.length || unloadingRouteLines.length)"
                class="reservation-route-block pb-3"
            >
                <div
                    v-for="(line, index) in loadingRouteLines"
                    :key="`load-line-${index}`"
                    class="d-flex align-center ga-2"
                >
                    <img
                        v-if="index === 0"
                        :src="loadingPng"
                        class="reservation-route-icon flex-shrink-0"
                        alt=""
                    />
                    <span v-else class="reservation-route-icon d-inline-block flex-shrink-0"></span>
                    <span class="reservation-route-line"><template v-if="loadingRouteLines.length > 1">{{ index + 1 }}. </template>{{ line }}</span>
                </div>
                <div
                    v-for="(line, index) in unloadingRouteLines"
                    :key="`unload-line-${index}`"
                    class="d-flex align-center ga-2"
                >
                    <img
                        v-if="index === 0"
                        :src="unloadingPng"
                        class="reservation-route-icon flex-shrink-0"
                        alt=""
                    />
                    <span v-else class="reservation-route-icon d-inline-block flex-shrink-0"></span>
                    <span class="reservation-route-line"><template v-if="unloadingRouteLines.length > 1">{{ index + 1 }}. </template>{{ line }}</span>
                </div>
            </div>
            <p>
                <strong>{{ $t('reservations.reservedBy') }}</strong>
                {{ reservedByText }}
            </p>
            <p>
                <strong>{{ $t('reservation.vehicleAndDriver') }}:</strong> {{ driverVehicleDisplay }} |
                {{ driverNameDisplay }}
            </p>
            <template v-if="showReservationPricingCopy">
                <template v-if="showCounterPendingReserver">
                    <p class="text-warning text-body-2">
                        <v-icon size="small" class="mr-1">mdi-alert</v-icon>
                        {{ $t("reservations.counterOfferPendingReserver") }}
                    </p>
                    <p class="text-body-2">
                        {{ $t("reservations.counterOfferPendingReserverDetailPart1") }}<strong>{{ offerPriceFormatted }}</strong>{{ $t("reservations.counterOfferPendingReserverDetailPart2", { companyName: ownerCompanyName }) }}<strong>{{ counterOfferFormatted }}</strong>.
                    </p>
                </template>
                <p
                    v-else-if="showReserverNegotiationResolved"
                    class="text-body-2"
                >
                    {{ $t("reservations.reserverNegotiationResolvedPart1") }}<strong>{{ offerPriceFormatted }}</strong>{{ $t("reservations.reserverNegotiationResolvedPart2", { companyName: ownerCompanyName }) }}<strong>{{ counterOfferFormatted }}</strong>.
                </p>
                <template v-else-if="showReserverInitialOfferNotice">
                    <p class="text-warning text-body-2 mb-1">
                        <v-icon size="small" class="mr-1">mdi-alert</v-icon>
                        {{
                            entryHasPrice
                                ? $t("reservations.pricingYouOfferedAttentionOnly")
                                : $t("reservations.pricingYouOfferedAttentionUnlisted")
                        }}
                    </p>
                    <p v-if="entryHasPrice" class="text-body-2 mb-0">
                        {{ $t("reservations.pricingYouOfferedDetailPart1") }}<strong>{{ entryPriceFormatted }}</strong>{{ $t("reservations.pricingYouOfferedDetailPart2") }}<strong>{{ offerPriceFormatted }}</strong>{{ $t("reservations.pricingYouOfferedDetailPart3", { companyName: ownerCompanyName }) }}
                    </p>
                    <p v-else class="text-body-2 mb-0">
                        {{ $t("reservations.pricingYouOfferedUnlistedPart1") }}<strong>{{ offerPriceFormatted }}</strong>{{ $t("reservations.pricingYouOfferedUnlistedPart2", { companyName: ownerCompanyName }) }}
                    </p>
                    <p v-if="hasLastCounterOfferAfterClear" class="text-body-2 mb-0">
                        {{ $t("reservations.pricingYouOfferedPreviousCounterPart1", { companyName: ownerCompanyName }) }}<strong>{{ counterOfferFormatted }}</strong>.
                    </p>
                </template>
                <template v-if="showOwnerIncomingOffer">
                    <p class="text-warning text-body-2 mb-1">
                        <v-icon size="small" class="mr-1">mdi-alert</v-icon>
                        {{
                            entryHasPrice
                                ? $t("reservations.pricingIncomingOfferIntro", { companyName: reserverCompanyName })
                                : $t("reservations.pricingIncomingOfferIntroUnlisted", { companyName: reserverCompanyName })
                        }}
                    </p>
                    <p v-if="entryHasPrice" class="text-body-2 mb-0">
                        {{ $t("reservations.pricingIncomingOfferMid") }}<strong>{{ entryPriceFormatted }}</strong>.
                        {{ $t("reservations.pricingIncomingOfferBeforeSuggested", { companyName: reserverCompanyName }) }}<strong>{{ offerPriceFormatted }}</strong>.
                    </p>
                    <p v-else class="text-body-2 mb-0">
                        {{ $t("reservations.pricingIncomingOfferUnlistedPart1", { companyName: reserverCompanyName }) }}<strong>{{ offerPriceFormatted }}</strong>{{ $t("reservations.pricingIncomingOfferUnlistedPart2") }}
                    </p>
                    <p v-if="hasLastCounterOfferAfterClear" class="text-body-2 mb-0">
                        {{ $t("reservations.pricingIncomingOfferPreviousCounterPart1") }}<strong>{{ counterOfferFormatted }}</strong>.
                    </p>
                </template>
                <template v-if="showCounterPendingOwner">
                    <p class="text-warning text-body-2">
                        <v-icon size="small" class="mr-1">mdi-alert</v-icon>
                        {{ $t("reservations.counterOfferPendingOwner", { companyName: reserverCompanyName }) }}
                    </p>
                    <p class="text-body-2">
                        {{ $t("reservations.counterOfferPendingOwnerDetailPart1", { companyName: reserverCompanyName }) }}<strong>{{ offerPriceFormatted }}</strong>{{ $t("reservations.counterOfferPendingOwnerDetailPart2") }}<strong>{{ counterOfferFormatted }}</strong>.
                    </p>
                </template>
                <p v-else-if="showOwnerNegotiationResolved" class="text-body-2">
                    {{ $t("reservations.counterOfferPendingOwnerDetailPart1", { companyName: reserverCompanyName }) }}<strong>{{ offerPriceFormatted }}</strong>{{ $t("reservations.counterOfferPendingOwnerDetailPart2") }}<strong>{{ counterOfferFormatted }}</strong>.
                </p>
                <p v-if="showCounterAcceptedOwnerNotice" class="text-success text-body-2">
                    {{ counterAcceptedOwnerText }}
                </p>
            </template>
            <template v-else-if="showPostPendingNegotiationSummary">
                <p v-if="postPendingAcceptedKind" class="text-success text-body-2">
                    <template v-if="postPendingAcceptedKind === 'offer'">
                        {{ $t("reservations.negotiationAcceptedOfferPart1") }}<strong>{{ postPendingAcceptedAmountFormatted }}</strong>{{ $t("reservations.negotiationAcceptedOfferPart2", { companyName: postPendingAcceptedByCompanyName, acceptedAt: postPendingAcceptedAtSuffix }) }}
                    </template>
                    <template v-else>
                        {{ $t("reservations.negotiationAcceptedCounterPart1") }}<strong>{{ postPendingAcceptedAmountFormatted }}</strong>{{ $t("reservations.negotiationAcceptedCounterPart2", { companyName: postPendingAcceptedByCompanyName, acceptedAt: postPendingAcceptedAtSuffix }) }}
                    </template>
                </p>
                <p v-else class="text-body-2">
                    <template v-if="displayOfferAmount != null && displayCounterOfferAmount != null">
                        {{
                            $t("reservations.negotiationSummaryAfterCloseBoth", {
                                offer: offerPriceFormatted,
                                counter: counterOfferFormatted,
                            })
                        }}
                    </template>
                    <template v-else-if="displayOfferAmount != null">
                        {{
                            $t("reservations.negotiationSummaryAfterCloseOfferOnly", {
                                offer: offerPriceFormatted,
                            })
                        }}
                    </template>
                    <template v-else>
                        {{
                            $t("reservations.negotiationSummaryAfterCloseCounterOnly", {
                                counter: counterOfferFormatted,
                            })
                        }}
                    </template>
                </p>
            </template>
            <v-row v-if="showTransportOrderIcons" density="compact" class="mt-2 gap-2" align="start">
                <v-col v-if="showTransportOrderIcon" cols="auto">
                    <input
                        ref="externalGeneratedPdfInput"
                        type="file"
                        accept=".pdf"
                        style="display: none"
                        @change="onExternalGeneratedPdfSelected"
                    />
                    <template v-if="hasTransportOrder">
                        <template v-if="transportOrderViewOnly">
                            <v-tooltip v-if="transportOrderPreview?.confirmed_at" :text="$t('reservations.transportOrderConfirmedTooltip')" location="top">
                                <template #activator="{ props: tooltipProps }">
                                    <span v-bind="tooltipProps" class="d-inline-block">
                                        <ReservationDocumentCard
                                            :has-document="hasTransportOrder"
                                            :title="$t('reservations.generatedTransportOrder')"
                                            :subtitle="transportOrderSubtitle"
                                            :subtitle-date="transportOrderSubtitleDate"
                                            :subtitle-time="transportOrderSubtitleTime"
                                            :clickable="false"
                                        >
                                            <template #actions>
                                                <v-tooltip location="top">
                                                    <template #activator="{ props }">
                                                        <v-icon size="small" class="cursor-pointer" v-bind="props" @click.stop="openPdf(transportOrderPreview.generated_pdf_url)">
                                                            mdi-eye-outline
                                                        </v-icon>
                                                    </template>
                                                    {{ $t('reservations.view') }}
                                                </v-tooltip>
                                            </template>
                                        </ReservationDocumentCard>
                                    </span>
                                </template>
                            </v-tooltip>
                            <span v-else class="d-inline-block">
                                <ReservationDocumentCard
                                    :has-document="hasTransportOrder"
                                    :title="$t('reservations.generatedTransportOrder')"
                                    :subtitle="transportOrderSubtitle"
                                            :subtitle-date="transportOrderSubtitleDate"
                                            :subtitle-time="transportOrderSubtitleTime"
                                    :clickable="false"
                                >
                                    <template #actions>
                                        <v-tooltip location="top">
                                            <template #activator="{ props }">
                                                <v-icon size="small" class="cursor-pointer" v-bind="props" @click.stop="openPdf(transportOrderPreview.generated_pdf_url)">
                                                    mdi-eye-outline
                                                </v-icon>
                                            </template>
                                            {{ $t('reservations.view') }}
                                        </v-tooltip>
                                    </template>
                                </ReservationDocumentCard>
                            </span>
                        </template>
                        <span v-else-if="!showTransportOrderMenu" class="d-inline-block">
                            <ReservationDocumentCard
                                        :has-document="hasTransportOrder"
                                        :title="$t('reservations.generatedTransportOrder')"
                                        :subtitle="transportOrderSubtitle"
                                            :subtitle-date="transportOrderSubtitleDate"
                                            :subtitle-time="transportOrderSubtitleTime"
                                        :clickable="true"
                                        :emit-click-on-click="true"
                                        @click="openPdf(transportOrderPreview.generated_pdf_url)"
                                    >
                                        <template #actions>
                                            <v-tooltip location="top">
                                                <template #activator="{ props }">
                                                    <v-icon size="small" class="cursor-pointer" v-bind="props" @click.stop="openPdf(transportOrderPreview.generated_pdf_url)">
                                                        mdi-eye-outline
                                                    </v-icon>
                                                </template>
                                                {{ $t('reservations.view') }}
                                            </v-tooltip>
                                        </template>
                                    </ReservationDocumentCard>
                        </span>
                        <v-menu v-else :close-on-content-click="true" location="bottom end">
                            <template #activator="{ props: menuProps }">
                                <v-tooltip :text="$t('reservations.transportOrderCardTooltip')" location="top">
                                    <template #activator="{ props: tooltipProps }">
                                        <span v-bind="{ ...menuProps, ...tooltipProps }" class="d-inline-block">
                                            <ReservationDocumentCard
                                        :has-document="hasTransportOrder"
                                        :title="$t('reservations.generatedTransportOrder')"
                                        :subtitle="transportOrderSubtitle"
                                            :subtitle-date="transportOrderSubtitleDate"
                                            :subtitle-time="transportOrderSubtitleTime"
                                        :clickable="true"
                                    >
                                        <template #actions>
                                            <v-tooltip location="top">
                                                <template #activator="{ props }">
                                                    <v-icon size="small" class="cursor-pointer" v-bind="props" @click.stop="openPdf(transportOrderPreview.generated_pdf_url)">
                                                        mdi-eye-outline
                                                    </v-icon>
                                                </template>
                                                {{ $t('reservations.view') }}
                                            </v-tooltip>
                                        </template>
                                    </ReservationDocumentCard>
                                        </span>
                                    </template>
                                </v-tooltip>
                            </template>
                            <v-list density="compact">
                                <v-list-item v-if="transportOrderPreview?.generated_pdf_url" @click="openPdf(transportOrderPreview.generated_pdf_url)">
                                    <v-list-item-title>{{ $t('reservations.download') }}</v-list-item-title>
                                </v-list-item>
                                <v-list-item v-if="transportOrderIsExternalPdf" @click="triggerExternalGeneratedPdfUpload">
                                    <v-list-item-title>{{ $t('reservations.replaceExternalFilePdf') }}</v-list-item-title>
                                </v-list-item>
                                <v-list-item v-if="transportOrderIsExternalPdf" @click="$emit('generateTransportOrder')">
                                    <v-list-item-title>{{ $t('reservations.generateTransportOrderFileFromApp') }}</v-list-item-title>
                                </v-list-item>
                                <v-list-item v-if="!transportOrderIsExternalPdf" @click="$emit('modifyTransportOrder')">
                                    <v-list-item-title>{{ $t('reservations.modify') }}</v-list-item-title>
                                </v-list-item>
                                <v-list-item v-if="!transportOrderIsExternalPdf" @click="triggerExternalGeneratedPdfUpload">
                                    <v-list-item-title>{{ $t('reservations.uploadExternalFilePdf') }}</v-list-item-title>
                                </v-list-item>
                                <v-list-item @click="$emit('sendTransportOrder')">
                                    <v-list-item-title>{{ $t('reservations.sendViaEmail') }}</v-list-item-title>
                                </v-list-item>
                                <v-list-item v-if="reserverWhatsAppUrl" @click="openWhatsApp(reserverWhatsAppUrl)">
                                    <v-list-item-title>{{ $t('reservations.sendViaWhatsApp') }}</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>
                    <template v-else>
                        <v-menu v-if="canGenerateTransportOrder" :close-on-content-click="true" location="bottom end">
                            <template #activator="{ props: menuProps }">
                                <v-tooltip :text="transportOrderNoFileTooltip" location="top">
                                    <template #activator="{ props: tooltipProps }">
                                        <span v-bind="{ ...menuProps, ...tooltipProps }" class="d-inline-block">
                                            <ReservationDocumentCard
                                                :has-document="hasTransportOrder"
                                                :title="$t('reservations.generatedTransportOrder')"
                                                :subtitle="transportOrderSubtitle"
                                                :subtitle-date="transportOrderSubtitleDate"
                                                :subtitle-time="transportOrderSubtitleTime"
                                                :clickable="true"
                                            />
                                        </span>
                                    </template>
                                </v-tooltip>
                            </template>
                            <v-list density="compact">
                                <v-list-item @click="$emit('generateTransportOrder')">
                                    <v-list-item-title>{{ $t('reservations.generateTransportOrder') }}</v-list-item-title>
                                </v-list-item>
                                <v-list-item @click="triggerExternalGeneratedPdfUpload">
                                    <v-list-item-title>{{ $t('reservations.uploadExternalFilePdf') }}</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                        <v-tooltip v-else location="top" :text="transportOrderNoFileTooltip">
                            <template #activator="{ props }">
                                <span v-bind="props" class="d-inline-block">
                                    <ReservationDocumentCard
                                        :has-document="hasTransportOrder"
                                        :title="$t('reservations.generatedTransportOrder')"
                                        :subtitle="transportOrderSubtitle"
                                        :subtitle-date="transportOrderSubtitleDate"
                                        :subtitle-time="transportOrderSubtitleTime"
                                        :clickable="false"
                                    />
                                </span>
                            </template>
                        </v-tooltip>
                    </template>
                </v-col>
                <v-col v-if="showSignedPdfIcon" cols="auto">
                    <input ref="signedPdfInput" type="file" accept=".pdf" style="display: none" @change="onSignedPdfSelected" />
                    <template v-if="signedPdfViewOnly">
                        <v-tooltip v-if="transportOrderPreview?.confirmed_at" :text="signedPdfConfirmedTooltip" location="top">
                            <template #activator="{ props: tooltipProps }">
                                <ReservationDocumentCard
                                    :has-document="!!transportOrderPreview?.has_signed_pdf"
                                    :title="$t('reservations.signedPdfInfo')"
                                    :subtitle="signedPdfSubtitle"
                                    :subtitle-date="signedPdfSubtitleDate"
                                    :subtitle-time="signedPdfSubtitleTime"
                                    :clickable="false"
                                    v-bind="tooltipProps"
                                >
                                    <template #actions>
                                        <v-tooltip location="top">
                                            <template #activator="{ props }">
                                                <v-icon size="small" class="cursor-pointer" v-bind="props" @click.stop="openPdf(transportOrderPreview.signed_pdf_url)">
                                                    mdi-eye-outline
                                                </v-icon>
                                            </template>
                                            {{ $t('reservations.view') }}
                                        </v-tooltip>
                                    </template>
                                </ReservationDocumentCard>
                            </template>
                        </v-tooltip>
                        <ReservationDocumentCard
                            v-else
                            :has-document="!!transportOrderPreview?.has_signed_pdf"
                            :title="$t('reservations.signedPdfInfo')"
                            :subtitle="signedPdfSubtitle"
                                    :subtitle-date="signedPdfSubtitleDate"
                                    :subtitle-time="signedPdfSubtitleTime"
                            :clickable="false"
                        >
                            <template #actions>
                                <v-tooltip location="top">
                                    <template #activator="{ props }">
                                        <v-icon size="small" class="cursor-pointer" v-bind="props" @click.stop="openPdf(transportOrderPreview.signed_pdf_url)">
                                            mdi-eye-outline
                                        </v-icon>
                                    </template>
                                    {{ $t('reservations.view') }}
                                </v-tooltip>
                            </template>
                        </ReservationDocumentCard>
                    </template>
                    <v-tooltip v-else-if="!transportOrderPreview?.has_signed_pdf" location="top" :text="signedPdfNoFileTooltip">
                        <template #activator="{ props }">
                            <span v-bind="props" class="d-inline-block">
                                <ReservationDocumentCard
                                    :has-document="!!transportOrderPreview?.has_signed_pdf"
                                    :title="$t('reservations.signedPdfInfo')"
                                    :subtitle="signedPdfSubtitle"
                                    :subtitle-date="signedPdfSubtitleDate"
                                    :subtitle-time="signedPdfSubtitleTime"
                                    :clickable="hasTransportOrder && canUploadSignedPdf"
                                    :emit-click-on-click="true"
                                    @click="onSignedPdfCardClick"
                                />
                            </span>
                        </template>
                    </v-tooltip>
                    <v-tooltip v-else-if="signedPdfUploadReplacementTooltip" location="top" :text="signedPdfUploadReplacementTooltip">
                        <template #activator="{ props }">
                            <span v-bind="props" class="d-inline-block">
                                <ReservationDocumentCard
                                    :has-document="!!transportOrderPreview?.has_signed_pdf"
                                    :title="$t('reservations.signedPdfInfo')"
                                    :subtitle="signedPdfSubtitle"
                                    :subtitle-date="signedPdfSubtitleDate"
                                    :subtitle-time="signedPdfSubtitleTime"
                                    :clickable="!isReservationDeclined"
                                    :emit-click-on-click="true"
                                    @click="onSignedPdfCardClick"
                                >
                        <template #actions>
                            <v-tooltip location="top">
                                <template #activator="{ props }">
                                    <v-icon size="small" class="cursor-pointer" v-bind="props" @click.stop="openPdf(transportOrderPreview.signed_pdf_url)">
                                        mdi-eye-outline
                                    </v-icon>
                                </template>
                                {{ $t('reservations.view') }}
                            </v-tooltip>
                        </template>
                                </ReservationDocumentCard>
                            </span>
                        </template>
                    </v-tooltip>
                    <ReservationDocumentCard
                        v-else
                        :has-document="!!transportOrderPreview?.has_signed_pdf"
                        :title="$t('reservations.signedPdfInfo')"
                        :subtitle="signedPdfSubtitle"
                        :subtitle-date="signedPdfSubtitleDate"
                        :subtitle-time="signedPdfSubtitleTime"
                        :clickable="!isReservationDeclined"
                        :emit-click-on-click="true"
                        @click="onSignedPdfCardClick"
                    >
                        <template #actions>
                            <v-tooltip location="top">
                                <template #activator="{ props }">
                                    <v-icon size="small" class="cursor-pointer" v-bind="props" @click.stop="openPdf(transportOrderPreview.signed_pdf_url)">
                                        mdi-eye-outline
                                    </v-icon>
                                </template>
                                {{ $t('reservations.view') }}
                            </v-tooltip>
                        </template>
                    </ReservationDocumentCard>
                </v-col>
                <v-col v-if="showCmrInvoiceSection" cols="auto">
                    <input ref="cmrPdfInput" type="file" accept=".pdf" style="display: none" @change="onCmrPdfSelected" />
                    <v-tooltip location="top" :disabled="!cmrTooltipText" :text="cmrTooltipText">
                        <template #activator="{ props }">
                            <span v-bind="props" class="d-inline-block">
                                <ReservationDocumentCard
                                    :has-document="!!transportOrderPreview?.has_cmr_pdf"
                                    :title="$t('reservations.cmrDocumentTitle')"
                                    :subtitle="cmrSubtitle"
                                    :subtitle-date="cmrSubtitleDate"
                                    :subtitle-time="cmrSubtitleTime"
                                    :clickable="cmrCardClickable"
                                    :emit-click-on-click="true"
                                    @click="onCmrCardClick"
                                >
                                    <template #actions>
                                        <v-tooltip v-if="transportOrderPreview?.cmr_pdf_url" location="top">
                                            <template #activator="{ props: ap }">
                                                <v-icon size="small" class="cursor-pointer" v-bind="ap" @click.stop="openPdf(transportOrderPreview.cmr_pdf_url)">
                                                    mdi-eye-outline
                                                </v-icon>
                                            </template>
                                            {{ $t('reservations.view') }}
                                        </v-tooltip>
                                    </template>
                                </ReservationDocumentCard>
                            </span>
                        </template>
                    </v-tooltip>
                </v-col>
                <v-col v-if="showCmrInvoiceSection" cols="auto">
                    <input ref="invoicePdfInput" type="file" accept=".pdf" style="display: none" @change="onInvoicePdfSelected" />
                    <v-tooltip location="top" :disabled="!invoiceTooltipText" :text="invoiceTooltipText">
                        <template #activator="{ props }">
                            <span v-bind="props" class="d-inline-block">
                                <ReservationDocumentCard
                                    :has-document="!!transportOrderPreview?.has_invoice_pdf"
                                    :title="$t('reservations.invoiceDocumentTitle')"
                                    :subtitle="invoiceSubtitle"
                                    :subtitle-date="invoiceSubtitleDate"
                                    :subtitle-time="invoiceSubtitleTime"
                                    :clickable="invoiceCardClickable"
                                    :emit-click-on-click="true"
                                    @click="onInvoiceCardClick"
                                >
                                    <template #actions>
                                        <v-tooltip v-if="transportOrderPreview?.invoice_pdf_url" location="top">
                                            <template #activator="{ props: ap }">
                                                <v-icon size="small" class="cursor-pointer" v-bind="ap" @click.stop="openPdf(transportOrderPreview.invoice_pdf_url)">
                                                    mdi-eye-outline
                                                </v-icon>
                                            </template>
                                            {{ $t('reservations.view') }}
                                        </v-tooltip>
                                    </template>
                                </ReservationDocumentCard>
                            </span>
                        </template>
                    </v-tooltip>
                </v-col>
                <v-col v-if="showCmrInvoiceSection" cols="auto">
                    <input
                        ref="paymentProofInput"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
                        style="display: none"
                        @change="onPaymentProofSelected"
                    />
                    <v-tooltip location="top" :disabled="!paymentProofTooltipText" :text="paymentProofTooltipText">
                        <template #activator="{ props }">
                            <span v-bind="props" class="d-inline-block">
                                <ReservationDocumentCard
                                    :has-document="!!transportOrderPreview?.has_payment_proof"
                                    :title="$t('reservations.paymentProofDocumentTitle')"
                                    :subtitle="paymentProofSubtitle"
                                    :subtitle-date="paymentProofSubtitleDate"
                                    :subtitle-time="paymentProofSubtitleTime"
                                    :clickable="paymentProofCardClickable"
                                    :emit-click-on-click="true"
                                    @click="onPaymentProofCardClick"
                                >
                                    <template #actions>
                                        <v-tooltip v-if="transportOrderPreview?.payment_proof_url" location="top">
                                            <template #activator="{ props: ap }">
                                                <v-icon size="small" class="cursor-pointer" v-bind="ap" @click.stop="openPdf(transportOrderPreview.payment_proof_url)">
                                                    mdi-eye-outline
                                                </v-icon>
                                            </template>
                                            {{ $t('reservations.view') }}
                                        </v-tooltip>
                                    </template>
                                </ReservationDocumentCard>
                            </span>
                        </template>
                    </v-tooltip>
                </v-col>
            </v-row>
            <v-row
                v-if="hasAnyCardActions"
                v-auto-animate
                no-gutters
                align="center"
                class="mt-auto pt-3 ga-2"
            >
                <v-col cols="12" sm class="d-flex flex-wrap align-center ga-2">
                    <template v-if="ownerPendingCounterOnly">
                        <v-btn color="error" size="small" @click="$emit('decline')">
                            {{ $t('reservations.decline') }}
                        </v-btn>
                    </template>
                    <template v-else-if="canConfirmReservation">
                        <v-btn color="error" size="small" @click="$emit('decline')">
                            {{ $t('reservations.decline') }}
                        </v-btn>
                        <template v-if="showOwnerCounterOfferControls">
                            <v-text-field
                                v-if="showCounterOfferInput"
                                :model-value="counterOfferValue"
                                style="max-width: 140px"
                                variant="outlined"
                                density="compact"
                                hide-details
                                type="number"
                                @update:modelValue="onCounterOfferModelValueUpdate"
                            />
                            <v-tooltip :disabled="!ownerCounterOfferDisabledTooltip" :text="ownerCounterOfferDisabledTooltip" location="top">
                                <template #activator="{ props: tipProps }">
                                    <span v-bind="tipProps" class="d-inline-block">
                                        <v-btn
                                            size="small"
                                            :disabled="sendCounterOfferDisabled || !negotiationCanSendCounter"
                                            @click="onCounterOfferButtonClick"
                                        >
                                            {{
                                                showCounterOfferInput
                                                    ? $t("reservations.counterOfferSend")
                                                    : $t("reservations.counterOfferMake")
                                            }}
                                        </v-btn>
                                    </span>
                                </template>
                            </v-tooltip>
                        </template>
                    </template>
                    <template v-else-if="canAcceptCounterOffer">
                        <template v-if="canCancel">
                            <v-tooltip
                                v-if="cancelDisabled"
                                :text="cancelDisabledTooltip"
                                location="top"
                            >
                                <template #activator="{ props }">
                                    <span v-bind="props" class="d-inline-block">
                                        <v-btn color="error" size="small" disabled>
                                            {{ $t('reservations.cancel') }}
                                        </v-btn>
                                    </span>
                                </template>
                            </v-tooltip>
                            <v-btn v-else color="error" size="small" @click="$emit('cancel')">
                                {{ $t('reservations.cancel') }}
                            </v-btn>
                        </template>
                        <template v-if="showReserverPriceOfferControls">
                            <v-text-field
                                v-if="showNewOfferInput"
                                :model-value="newOfferValue"
                                style="max-width: 140px"
                                variant="outlined"
                                density="compact"
                                hide-details
                                type="number"
                                @update:modelValue="onNewOfferModelValueUpdate"
                            />
                            <v-tooltip :disabled="!reserverOfferDisabledTooltip" :text="reserverOfferDisabledTooltip" location="top">
                                <template #activator="{ props: tipProps }">
                                    <span v-bind="tipProps" class="d-inline-block">
                                        <v-btn
                                            size="small"
                                            color="primary"
                                            :disabled="sendNewOfferDisabled || !negotiationCanSendOffer"
                                            @click="onNewOfferButtonClick"
                                        >
                                            {{
                                                showNewOfferInput
                                                    ? $t("reservations.sendNewPriceOffer")
                                                    : $t("reservations.makeNewPriceOffer")
                                            }}
                                        </v-btn>
                                    </span>
                                </template>
                            </v-tooltip>
                            <span
                                v-if="negotiationLimitDisplay"
                                class="text-caption text-medium-emphasis"
                            >{{ negotiationLimitDisplay }}</span>
                        </template>
                    </template>
                    <template v-else>
                        <template v-if="canCancel">
                            <v-tooltip
                                v-if="cancelDisabled"
                                :text="cancelDisabledTooltip"
                                location="top"
                            >
                                <template #activator="{ props }">
                                    <span v-bind="props" class="d-inline-block">
                                        <v-btn color="error" size="small" disabled>
                                            {{ $t('reservations.cancel') }}
                                        </v-btn>
                                    </span>
                                </template>
                            </v-tooltip>
                            <v-btn v-else color="error" size="small" @click="$emit('cancel')">
                                {{ $t('reservations.cancel') }}
                            </v-btn>
                        </template>
                        <template v-if="showReserverPriceOfferControls">
                            <v-text-field
                                v-if="showNewOfferInput"
                                :model-value="newOfferValue"
                                style="max-width: 140px"
                                variant="outlined"
                                density="compact"
                                hide-details
                                type="number"
                                @update:modelValue="onNewOfferModelValueUpdate"
                            />
                            <v-tooltip :disabled="!reserverOfferDisabledTooltip" :text="reserverOfferDisabledTooltip" location="top">
                                <template #activator="{ props: tipProps }">
                                    <span v-bind="tipProps" class="d-inline-block">
                                        <v-btn
                                            size="small"
                                            color="primary"
                                            :disabled="sendNewOfferDisabled || !negotiationCanSendOffer"
                                            @click="onNewOfferButtonClick"
                                        >
                                            {{
                                                showNewOfferInput
                                                    ? $t("reservations.sendNewPriceOffer")
                                                    : $t("reservations.makeNewPriceOffer")
                                            }}
                                        </v-btn>
                                    </span>
                                </template>
                            </v-tooltip>
                            <span
                                v-if="negotiationLimitDisplay"
                                class="text-caption text-medium-emphasis"
                            >{{ negotiationLimitDisplay }}</span>
                        </template>
                    </template>
                    <template v-if="canConfirmTransportOrder">
                        <v-btn
                            size="small"
                            :disabled="!!transportOrderPreview?.confirmed_at"
                            @click="$emit('confirmTransportOrder')"
                        >
                            {{ $t('reservations.confirmTransportOrder') }}
                        </v-btn>
                    </template>
                    <template v-if="canConfirmCompleted">
                        <v-btn size="small" @click="$emit('confirmCompleted')">
                            {{ $t('reservations.confirmCompleted') }}
                        </v-btn>
                    </template>
                    <template v-if="showRatingForm">
                        <v-tooltip
                            location="top"
                            :open-delay="250"
                            :text="ratingTooltipSubmit"
                            :disabled="!ratingTooltipSubmit"
                        >
                            <template #activator="{ props: tipProps }">
                                <div v-bind="tipProps" class="d-flex align-center">
                                    <star-rating-input
                                        v-model="quickRatingStars"
                                        :star-size="20"
                                        @update:model-value="onQuickRatingSelected"
                                    />
                                </div>
                            </template>
                        </v-tooltip>
                    </template>
                    <template v-if="showEditRating">
                        <v-tooltip
                            location="top"
                            :open-delay="250"
                            :text="ratingTooltipEdit"
                            :disabled="!ratingTooltipEdit"
                        >
                            <template #activator="{ props: tipProps }">
                                <span v-bind="tipProps" class="d-inline-block">
                                    <v-btn size="small" @click="ratingModalVisible = true">
                                        {{ $t('rating.editTitle') }}
                                    </v-btn>
                                </span>
                            </template>
                        </v-tooltip>
                    </template>
                </v-col>
                <v-col
                    v-if="ownerPendingCounterOnly || canConfirmReservation || canAcceptCounterOffer"
                    cols="12"
                    sm="auto"
                    class="d-flex justify-end"
                >
                    <v-btn v-if="ownerPendingCounterOnly" size="small" color="success" disabled>
                        {{ $t('reservations.confirm') }}
                    </v-btn>
                    <v-btn v-else-if="canConfirmReservation" size="small" color="success" @click="onConfirmClick">
                        {{ $t('reservations.confirm') }}
                    </v-btn>
                    <v-btn
                        v-else
                        color="success"
                        size="small"
                        @click="$emit('accept-price-counter-offer')"
                    >
                        {{ $t("reservations.acceptCounterOffer") }}
                    </v-btn>
                </v-col>
            </v-row>
            <v-dialog v-model="ratingModalVisible" max-width="500" scrim="black">
                <RatingForm
                    v-if="ratingModalVisible && (showRatingForm || showEditRating)"
                    :rated-company-id="reservation.rated_company_id"
                    :reservation-uid="reservation.uid"
                    :existing-rating="showEditRating ? reservation.existing_rating : null"
                    :initial-stars="quickRatingStars"
                    :mode="showEditRating ? 'edit' : 'create'"
                    @rated="onRatingSubmitted"
                    @cancel="onRatingCancel"
                />
            </v-dialog>
            <v-dialog v-model="truckOwnerModalVisible" max-width="560" scrim="black">
                <v-card v-if="truckOwnerModalVisible">
                    <v-card-title class="text-wrap px-6 pt-6">
                        {{
                            truckOwnerModalMode === "confirm"
                                ? $t("reservations.truckOwnerModalConfirmTitle")
                                : $t("reservations.truckOwnerModalCounterTitle")
                        }}
                    </v-card-title>
                    <v-card-text class="px-6">
                        <template v-if="truckOwnerModalMode === 'counter'">
                            <p class="text-body-2 mb-2">
                                <template v-if="hasPreviousCounterOffer">
                                    {{ $t("reservations.truckOwnerModalCounterCopyWithPreviousPart1") }}
                                    <strong>{{ counterOfferFormatted }}</strong>.
                                    {{ $t("reservations.truckOwnerModalCounterCopyWithPreviousPart2", { companyName: reserverCompanyName }) }}
                                    <strong>{{ offerPriceFormatted }}</strong>.
                                </template>
                                <template v-else-if="entryHasPrice">
                                    {{ $t("reservations.truckOwnerModalCounterCopyInitialPart1") }}
                                    <strong>{{ entryPriceFormatted }}</strong>.
                                    {{ $t("reservations.truckOwnerModalCounterCopyInitialPart2", { companyName: reserverCompanyName }) }}
                                    <strong>{{ offerPriceFormatted }}</strong>.
                                </template>
                                <template v-else>
                                    {{ $t("reservations.truckOwnerModalCounterCopyUnlistedPart1", { companyName: reserverCompanyName }) }}
                                    <strong>{{ offerPriceFormatted }}</strong>{{ $t("reservations.truckOwnerModalCounterCopyUnlistedPart2") }}
                                </template>
                            </p>
                            <p class="text-body-2 mb-3">
                                {{ $t("reservations.truckOwnerModalCounterSendingPart1") }}
                                <strong>{{ formatNegotiationLine(pendingTruckCounterAmount, negotiationDisplayTerms) }}</strong>.
                            </p>
                        </template>
                        <ReservationDriverFields
                            :model-value="truckModalDriver"
                            density="compact"
                            required
                            @update:model-value="onTruckModalDriverUpdate"
                        />
                    </v-card-text>
                    <v-card-actions class="px-6 pb-6">
                        <v-row dense class="w-100 ma-0">
                            <v-col cols="6" class="pa-1">
                                <v-btn block color="error" variant="flat" @click="truckOwnerModalVisible = false">
                                    {{ $t("reservation.cancel") }}
                                </v-btn>
                            </v-col>
                            <v-col cols="6" class="pa-1">
                                <v-btn
                                    block
                                    :color="truckOwnerModalMode === 'confirm' ? 'success' : 'primary'"
                                    variant="flat"
                                    @click="submitTruckOwnerModal"
                                >
                                    {{ truckOwnerModalMode === "confirm" ? $t("reservations.confirm") : $t("reservations.counterOfferSend") }}
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-card-actions>
                </v-card>
            </v-dialog>
                </v-col>
                <v-col
                    cols="12"
                    sm="auto"
                    align-self="start"
                    class="reservation-card-contact-col mt-4 mt-sm-0 ms-sm-4"
                >
                    <div
                        class="reservation-card-contact-stack d-flex flex-row flex-sm-column justify-center justify-sm-end align-center align-sm-end ga-2"
                    >
                        <div v-if="showReservationScheduleEditContactButton" class="d-flex align-center">
                            <v-tooltip location="top">
                                <template #activator="{ props: scheduleEditTooltipProps }">
                                    <v-btn
                                        icon
                                        size="x-large"
                                        elevation="3"
                                        color="white"
                                        density="compact"
                                        v-bind="scheduleEditTooltipProps"
                                        @click.stop="$emit('edit-reservation-schedule')"
                                    >
                                        <v-icon>mdi-pencil</v-icon>
                                    </v-btn>
                                </template>
                                {{ $t("reservations.editCardSchedule") }}
                            </v-tooltip>
                        </div>
                        <ContactSection
                            :user="contactUser"
                            :form-type="reservation.entry_summary?.form_type || entryType || 'truck'"
                            :prefill-message="whatsAppContactPrefillMessage"
                        />
                        <div class="d-flex align-center">
                            <v-tooltip location="left">
                                <template #activator="{ props: chatTooltipProps }">
                                    <v-btn
                                        icon
                                        size="x-large"
                                        elevation="3"
                                        color="white"
                                        density="compact"
                                        v-bind="chatTooltipProps"
                                        :aria-label="$t('reservations.chat.title')"
                                        @click.stop="chatExpanded = !chatExpanded"
                                    >
                                        <v-badge
                                            :model-value="chatUnreadCount > 0 && !chatExpanded"
                                            :content="chatUnreadCount > 9 ? '9+' : String(chatUnreadCount)"
                                            color="error"
                                            floating
                                        >
                                            <v-icon :color="chatExpanded ? 'primary' : undefined">
                                                {{ chatExpanded ? 'mdi-message-text' : 'mdi-message-text-outline' }}
                                            </v-icon>
                                        </v-badge>
                                    </v-btn>
                                </template>
                                {{ $t("reservations.chat.title") }}
                            </v-tooltip>
                        </div>
                    </div>
                </v-col>
            </v-row>
            </div>
            <ReservationChatPanel
                v-model:expanded="chatExpanded"
                :reservation-uid="String(reservation.uid)"
                :unread-count="chatUnreadCount"
                :reservation-number="reservation.entry_summary?.identification_id || reservation.uid"
                :entry-search-url="entrySearchUrl"
                @read="$emit('chat-read', $event)"
            />
        </div>
        </template>
    </BulletCard>
</template>

<script>
import BulletCard from "@/components/display/BulletCard.vue";
import ContactSection from "@/components/ContactSection.vue";
import StatusBadge from "./StatusBadge.vue";
import RatingForm from "@/components/rating/RatingForm.vue";
import StarRatingInput from "@/components/rating/StarRatingInput.vue";
import ReservationDocumentCard from "./ReservationDocumentCard.vue";
import ReservationDriverFields from "./ReservationDriverFields.vue";
import ReservationChatPanel from "./ReservationChatPanel.vue";
import loadingPng from "@/assets/Loading.png";
import unloadingPng from "@/assets/Unloading.png";
import { mapState } from "vuex";
import {
    cleanPriceInput,
    formatDisplayDate,
    formatDisplayDateTime,
    formatDisplayTime,
    getRatingWindowRemainingParts,
    showAlertModal,
} from "@/utils";
import {
    buildReservationWhatsAppContactMessage,
    countryDisplayName,
    formatReservationContactWhen,
} from "@/utils/messagingContact";

const RESERVATION_DOCUMENT_MAX_BYTES = 5 * 1024 * 1024;

/**
 * After confirmation: keep transport-order + signed-PDF document lane usable even when dates slip
 * (e.g. in_progress without files yet). Matches backend CMR eligibility from confirmed onward.
 */
const STATUSES_TRANSPORT_DOCUMENT_POST_CONFIRM_FLOW = [
    "waiting_for_signed_to",
    "waiting_for_confirmation",
    "confirmed",
    "in_progress",
    "completed_and_awaiting_confirmation",
    "completed_and_confirmed",
];

export default {
    components: {
        BulletCard,
        ContactSection,
        StatusBadge,
        RatingForm,
        ReservationDocumentCard,
        ReservationDriverFields,
        ReservationChatPanel,
        StarRatingInput,
    },
    data() {
        return {
            loadingPng,
            unloadingPng,
            ratingModalVisible: false,
            quickRatingStars: 0,
            truckCapacityHintShown: false,
            truckOwnerModalVisible: false,
            truckOwnerModalMode: "confirm",
            truckModalDriver: {
                driver_name: "",
                driver_surname: "",
                car_number: "",
                customs: "",
            },
            pendingTruckCounterAmount: null,
            showCounterOfferInput: false,
            counterOfferValue: null,
            showNewOfferInput: false,
            newOfferValue: null,
            chatExpanded: false,
        };
    },
    watch: {
        "reservation.uid"() {
            this.showCounterOfferInput = false;
            this.counterOfferValue = null;
            this.showNewOfferInput = false;
            this.newOfferValue = null;
            this.truckOwnerModalVisible = false;
            this.chatExpanded = false;
            this.prefillTruckModalFromReservation();
        },
        "reservation.price_offer_sent_at"() {
            // When the other party sends a new offer, close any draft counter-offer UI.
            this.showCounterOfferInput = false;
            this.counterOfferValue = null;
            this.pendingTruckCounterAmount = null;
        },
        "reservation.price_offer_count"() {
            this.showNewOfferInput = false;
            this.newOfferValue = null;
        },
        showTruckCapacityHint: {
            handler(show) {
                if (show && !this.truckCapacityHintShown) {
                    this.truckCapacityHintShown = true;
                    showAlertModal(this.$store, this.$t("reservations.truckCapacityHint"), "info", 10000);
                }
            },
        },
    },
    mounted() {
        this.prefillTruckModalFromReservation();
    },
    props: {
        reservation: { type: Object, required: true },
        entryType: { type: String, required: true },
        isEntryOwner: { type: Boolean, default: false },
        chatUnreadCount: { type: Number, default: 0 },
    },
    emits: [
        "confirm",
        "decline",
        "cancel",
        "generateTransportOrder",
        "downloadTransportOrder",
        "modifyTransportOrder",
        "sendTransportOrder",
        "confirmTransportOrder",
        "confirmCompleted",
        "rated",
        "uploadSignedPdf",
        "uploadCmr",
        "uploadInvoice",
        "uploadPaymentProof",
        "uploadGeneratedTransportOrder",
        "price-counter-offer",
        "accept-price-counter-offer",
        "price-offer",
        "edit-reservation-schedule",
        "chat-read",
    ],
    computed: {
        ...mapState(["countries"]),
        showReservationScheduleEdit() {
            if (this.isReservationDeclined) return false;
            const truckListingOwnerSchedulesReservation =
                this.entryType === "truck" && this.isEntryOwner;
            const wareTransporterSchedulesReservation =
                this.entryType === "ware" && !this.isEntryOwner;
            return truckListingOwnerSchedulesReservation || wareTransporterSchedulesReservation;
        },
        /** Same visibility as before: only when the route summary block is shown. */
        showReservationScheduleEditContactButton() {
            return (
                this.showReservationScheduleEdit &&
                this.reservation.entry_summary &&
                !!(this.loadingRouteLine || this.unloadingRouteLine)
            );
        },
        reserverCompanyName() {
            return this.reservation.reserved_by_company?.name || "—";
        },
        ownerCompanyName() {
            return this.reservation.owner_company_name || "—";
        },
        displayOfferAmount() {
            const reservationData = this.reservation;
            if (reservationData.price_offer != null) return reservationData.price_offer;
            return reservationData.last_offer_amount ?? null;
        },
        displayCounterOfferAmount() {
            const reservationData = this.reservation;
            if (reservationData.price_counter_offer != null) return reservationData.price_counter_offer;
            return reservationData.last_counter_offer_amount ?? null;
        },
        offerPriceFormatted() {
            return this.formatNegotiationLine(this.displayOfferAmount, this.negotiationDisplayTerms);
        },
        counterOfferFormatted() {
            return this.formatNegotiationLine(this.displayCounterOfferAmount, this.negotiationDisplayTerms);
        },
        hasPreviousCounterOffer() {
            return this.displayCounterOfferAmount != null;
        },
        hasLastCounterOfferAfterClear() {
            return (
                this.reservation.price_counter_offer == null &&
                this.reservation.last_counter_offer_amount != null
            );
        },
        entryHasPrice() {
            const entryPrice = this.reservation.entry_summary?.entry_price;
            return entryPrice != null && entryPrice !== "";
        },
        entryPriceFormatted() {
            const entrySummary = this.reservation.entry_summary;
            if (!entrySummary || entrySummary.entry_price == null || entrySummary.entry_price === "") return "";
            const currencyCode = entrySummary.entry_currency || "";
            const vatLabel =
                entrySummary.entry_vat_type === true
                    ? this.$t("formFields.withVAT")
                    : this.$t("formFields.withoutVAT");
            return `${entrySummary.entry_price} ${currencyCode} ${vatLabel}`.trim();
        },
        entryTermsForDisplay() {
            if (!this.entryHasPrice) return null;
            const es = this.reservation.entry_summary;
            if (!es) return null;
            const currencyCode = es.entry_currency || "";
            const vatRaw = es.entry_vat_type;
            const vatType = vatRaw === true ? true : vatRaw === false ? false : null;
            if (!currencyCode && vatType !== true && vatType !== false) return null;
            return { currency: currencyCode, vatType };
        },
        reserverOfferTermsForDisplay() {
            const r = this.reservation;
            const cur = r.last_offer_currency ?? r.price_offer_currency ?? null;
            const vatRaw = r.last_offer_vat_type ?? r.price_offer_vat_type;
            const vatType = vatRaw === true ? true : vatRaw === false ? false : null;
            if (!cur || (vatType !== true && vatType !== false)) return null;
            return { currency: cur, vatType };
        },
        negotiationDisplayTerms() {
            if (this.entryHasPrice) return this.entryTermsForDisplay;
            return this.reserverOfferTermsForDisplay;
        },
        counterOfferPending() {
            const reservationData = this.reservation;
            return (
                reservationData.price_counter_offer != null &&
                reservationData.price_counter_offer_accepted_at == null
            );
        },
        ownerPending() {
            return this.isEntryOwner && this.reservation.status === "pending";
        },
        canConfirmReservation() {
            return this.ownerPending && !this.counterOfferPending;
        },
        ownerPendingCounterOnly() {
            return this.ownerPending && this.counterOfferPending;
        },
        negotiationCanSendOffer() {
            if (this.reservation.negotiation_can_send_offer === true) return true;
            return false;
        },
        negotiationCanSendCounter() {
            return this.reservation.negotiation_can_send_counter === true;
        },
        ownerCounterOfferDisabledTooltip() {
            if (this.negotiationCanSendCounter) return "";
            const perSideLimit = this.reservation.offer_counter_offer_limit;
            if (perSideLimit > 0 && (this.reservation.price_counter_offer_count ?? 0) >= perSideLimit) {
                return this.$t("reservations.negotiationLimitCounterOffers");
            }
            return this.$t("reservations.negotiationCounterNotYet");
        },
        reserverOfferDisabledTooltip() {
            if (this.negotiationCanSendOffer) return "";
            const perSideLimit = this.reservation.offer_counter_offer_limit;
            if (perSideLimit > 0 && (this.reservation.price_offer_count ?? 0) >= perSideLimit) {
                return this.$t("reservations.negotiationLimitOffers");
            }
            return this.$t("reservations.negotiationOfferNotYet");
        },
        negotiationLimitDisplay() {
            const perSideLimit = this.reservation.offer_counter_offer_limit;
            if (perSideLimit == null || perSideLimit === 0) return "";
            const offerCount = this.reservation.price_offer_count ?? 0;
            const counterOfferCount = this.reservation.price_counter_offer_count ?? 0;
            return this.$t("reservations.negotiationCountSummary", {
                offerCount,
                offerLimit: perSideLimit,
                counterCount: counterOfferCount,
                counterLimit: perSideLimit,
            });
        },
        showReserverPriceOfferControls() {
            return (
                !this.isEntryOwner &&
                this.reservation.status === "pending" &&
                this.negotiationCanSendOffer
            );
        },
        sendNewOfferDisabled() {
            if (!this.showNewOfferInput) return false;
            if (this.newOfferValue == null || this.newOfferValue <= 0) return true;
            if (!this.entryHasPrice) {
                const cur =
                    this.reservation.last_offer_currency || this.reservation.price_offer_currency;
                const vat =
                    this.reservation.last_offer_vat_type ?? this.reservation.price_offer_vat_type;
                if (!cur || (vat !== true && vat !== false)) return true;
            }
            return false;
        },
        hasNegotiationSnapshot() {
            return (
                this.displayOfferAmount != null ||
                this.displayCounterOfferAmount != null ||
                this.reservation.price_counter_offer_accepted_at != null ||
                this.reservation.price_offer_accepted_at != null
            );
        },
        showReservationPricingCopy() {
            if (this.reservation.status !== "pending" || !this.hasNegotiationSnapshot) return false;
            if (this.entryHasPrice) return true;
            return (
                this.displayOfferAmount != null ||
                this.displayCounterOfferAmount != null
            );
        },
        showPostPendingNegotiationSummary() {
            if (this.reservation.status === "pending") return false;
            if (this.reservation.status === "declined") return false;
            return this.hasNegotiationSnapshot;
        },
        postPendingAcceptedKind() {
            if (this.reservation.price_counter_offer_accepted_at) return "counter";
            if (this.reservation.price_offer_accepted_at) return "offer";
            return null;
        },
        postPendingAcceptedTimestamp() {
            if (this.reservation.price_counter_offer_accepted_at) return this.reservation.price_counter_offer_accepted_at;
            if (this.reservation.price_offer_accepted_at) return this.reservation.price_offer_accepted_at;
            return null;
        },
        postPendingAcceptedAmount() {
            if (this.postPendingAcceptedKind === "counter") {
                return this.displayCounterOfferAmount ?? this.reservation.price ?? null;
            }
            if (this.postPendingAcceptedKind === "offer") {
                return this.displayOfferAmount ?? this.reservation.price ?? null;
            }
            return null;
        },
        postPendingAcceptedAmountFormatted() {
            return this.formatNegotiationLine(this.postPendingAcceptedAmount, this.negotiationDisplayTerms);
        },
        postPendingAcceptedByCompanyName() {
            if (this.postPendingAcceptedKind === "counter") return this.reserverCompanyName;
            if (this.postPendingAcceptedKind === "offer") return this.ownerCompanyName;
            return "—";
        },
        postPendingAcceptedAtSuffix() {
            const acceptedTimestamp = this.postPendingAcceptedTimestamp;
            return acceptedTimestamp ? ` (${formatDisplayDateTime(acceptedTimestamp)})` : "";
        },
        showReserverInitialOfferNotice() {
            return (
                !this.isEntryOwner &&
                this.reservation.status === "pending" &&
                this.reservation.price_offer != null &&
                !this.counterOfferPending &&
                !this.reservation.price_counter_offer_accepted_at
            );
        },
        showReserverNegotiationResolved() {
            return (
                !this.isEntryOwner &&
                this.reservation.status === "pending" &&
                !!this.reservation.price_counter_offer_accepted_at &&
                this.reservation.price_offer != null
            );
        },
        showOwnerIncomingOffer() {
            return (
                this.isEntryOwner &&
                this.reservation.status === "pending" &&
                this.reservation.price_offer != null &&
                !this.counterOfferPending &&
                !this.reservation.price_counter_offer_accepted_at
            );
        },
        showOwnerNegotiationResolved() {
            return (
                this.isEntryOwner &&
                this.reservation.status === "pending" &&
                !!this.reservation.price_counter_offer_accepted_at &&
                this.reservation.price_offer != null &&
                this.reservation.price_counter_offer != null
            );
        },
        showCounterPendingReserver() {
            return (
                !this.isEntryOwner &&
                this.reservation.status === "pending" &&
                this.counterOfferPending
            );
        },
        showCounterPendingOwner() {
            return (
                this.isEntryOwner &&
                this.reservation.status === "pending" &&
                this.counterOfferPending
            );
        },
        showCounterAcceptedOwnerNotice() {
            return (
                this.isEntryOwner &&
                this.reservation.status === "pending" &&
                !!this.reservation.price_counter_offer_accepted_at &&
                !this.counterOfferPending
            );
        },
        counterAcceptedOwnerText() {
            const acceptedTimestamp = this.reservation.price_counter_offer_accepted_at;
            const acceptedAt = acceptedTimestamp ? ` (${formatDisplayDateTime(acceptedTimestamp)})` : "";
            return this.$t("reservations.counterOfferAcceptedOwner", { acceptedAt });
        },
        canAcceptCounterOffer() {
            return !this.isEntryOwner && this.reservation.status === "pending" && this.counterOfferPending;
        },
        showOwnerCounterOfferControls() {
            const reservationData = this.reservation;
            return (
                this.isEntryOwner &&
                this.canConfirmReservation &&
                reservationData.price_offer != null &&
                !reservationData.price_counter_offer_accepted_at &&
                !this.counterOfferPending
            );
        },
        sendCounterOfferDisabled() {
            if (!this.showCounterOfferInput) return false;
            return this.counterOfferValue == null || this.counterOfferValue <= 0;
        },
        contactUser() {
            if (this.isEntryOwner) return this.reservation.reserved_by_user || null;
            return this.reservation.owner_user || null;
        },
        transportOrderPreview() {
            return this.reservation.transport_order_preview || null;
        },
        transportOrderNumber() {
            const transportOrderNumberFromPreview = this.transportOrderPreview?.transport_order_number;
            const trimmedNumber =
                transportOrderNumberFromPreview != null ? String(transportOrderNumberFromPreview).trim() : "";
            return trimmedNumber || null;
        },
        canConfirmTransportOrder() {
            if (this.reservation.status !== "waiting_for_confirmation" || !this.reservation.has_transport_order || !this.transportOrderPreview?.has_signed_pdf) return false;
            return this.isTruckReservation ? !this.isEntryOwner : this.isEntryOwner;
        },
        loadingRouteLine() {
            return this.loadingRouteLines[0] || "";
        },
        unloadingRouteLine() {
            return this.unloadingRouteLines[this.unloadingRouteLines.length - 1] || "";
        },
        loadingRouteLines() {
            return this.routeLinesForSide("loading");
        },
        unloadingRouteLines() {
            return this.routeLinesForSide("unloading");
        },
        reservedByText() {
            const reservedByUser = this.reservation.reserved_by_user;
            const reservedByCompany = this.reservation.reserved_by_company;
            if (!reservedByUser) return "-";
            const name = [reservedByUser.first_name, reservedByUser.last_name].filter(Boolean).join(" ") || "-";
            // const email = reservedByUser.email || "";
            const companyName = reservedByCompany?.name || "";
            return `${name} ${companyName ? ` - ${companyName}` : ""}`;
        },
        isTruckReservation() {
            return (this.reservation.entry_summary?.form_type || this.entryType) === "truck";
        },
        whatsAppContactPrefillMessage() {
            const entrySummary = this.reservation.entry_summary;
            if (!entrySummary) return "";
            const formType = entrySummary.form_type || this.entryType || "truck";
            const identificationId =
                entrySummary.identification_id || this.reservation.uid || "";
            const loadingLocation = entrySummary.loading_location || {};
            const unloadingLocation = entrySummary.unloading_location || {};
            return buildReservationWhatsAppContactMessage({
                formType,
                id: identificationId,
                fromCountry: countryDisplayName(
                    this.countries,
                    loadingLocation.country_code
                ),
                fromCity: loadingLocation.city || "",
                fromWhen: formatReservationContactWhen(
                    entrySummary.loading_scheduled_date,
                    entrySummary.loading_scheduled_time
                ),
                toCountry: countryDisplayName(
                    this.countries,
                    unloadingLocation.country_code
                ),
                toCity: unloadingLocation.city || "",
                toWhen: formatReservationContactWhen(
                    entrySummary.unloading_scheduled_date,
                    entrySummary.unloading_scheduled_time
                ),
            });
        },
        driverVehicleDisplay() {
            if (this.isTruckReservation && !(this.reservation.car_number || "").trim()) {
                return this.$t("reservation.toBeFilledByOwner");
            }
            return this.reservation.car_number || "—";
        },
        driverNameDisplay() {
            if (this.isTruckReservation && !(this.reservation.driver_name || "").trim() && !(this.reservation.driver_surname || "").trim()) {
                return this.$t("reservation.toBeFilledByOwner");
            }
            const name = [this.reservation.driver_name, this.reservation.driver_surname].filter(Boolean).join(" ").trim();
            return name || "—";
        },
        reserverWhatsAppUrl() {
            const user = this.reservation.reserved_by_user;
            if (!user?.phone || !user?.hasWhatsApp) return null;
            const prefix = (user.phone_prefix || "").replace(/\D/g, "") || "";
            let number = (user.phone || "").replace(/\D/g, "") || "";
            if (!number) return null;
            if (prefix) number = number.replace(/^0+/, "") || number;
            const full = prefix + number;
            const pdfUrl = this.transportOrderPreview?.generated_pdf_url;
            const text = pdfUrl
                ? encodeURIComponent(this.$t("reservations.whatsAppMessageWithPdf", { url: pdfUrl }))
                : "";
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            if (isMobile) {
                return `https://wa.me/${full}${text ? `?text=${text}` : ""}`;
            }
            return `https://web.whatsapp.com/send?phone=${full}${text ? `&text=${text}` : ""}`;
        },
        canConfirmWithDriverInfo() {
            return true;
        },
        canCancel() {
            if (["in_progress", "completed_and_awaiting_confirmation", "completed_and_confirmed", "declined"].includes(this.reservation.status)) return false;
            if (this.isEntryOwner && this.reservation.status === "pending") return false;
            return true;
        },
        cancelDisabled() {
            return !!this.transportOrderPreview?.confirmed_at;
        },
        cancelDisabledTooltip() {
            const name = this.transportOrderPreview?.confirmed_by_company_name || "-";
            return this.$t("reservations.cancelDisabledTooltip", { companyName: name });
        },
        canConfirmCompleted() {
            if (this.reservation.status !== "completed_and_awaiting_confirmation") return false;
            const isOwner = this.isEntryOwner;
            const isReserver = !this.isEntryOwner;
            if (isOwner && this.reservation.completed_confirmed_by_owner) return false;
            if (isReserver && this.reservation.completed_confirmed_by_reserver) return false;
            return true;
        },
        awaitingConfirmationTooltip() {
            if (this.reservation.status !== "completed_and_awaiting_confirmation") return "";
            const ownerName = this.reservation.owner_company_name || "—";
            const reserverName = this.reservation.reserved_by_company?.name || "—";
            const bothConfirmed = this.reservation.completed_confirmed_by_owner && this.reservation.completed_confirmed_by_reserver;
            if (bothConfirmed) return "";
            const neitherConfirmed = !this.reservation.completed_confirmed_by_owner && !this.reservation.completed_confirmed_by_reserver;
            if (neitherConfirmed) {
                return this.$t("reservations.awaitingConfirmationTooltipBoth", { owner: ownerName, reserver: reserverName });
            }
            if (!this.reservation.completed_confirmed_by_owner) {
                return this.$t("reservations.awaitingConfirmationTooltipFrom", { company: ownerName });
            }
            return this.$t("reservations.awaitingConfirmationTooltipFrom", { company: reserverName });
        },
        showRatingForm() {
            if (!this.reservation.rated_company_id) return false;
            if (this.reservation.user_has_rated) return false;
            if (typeof this.reservation.rating_can_submit === "boolean") {
                return this.reservation.rating_can_submit;
            }
            const statusOk = ["completed_and_awaiting_confirmation", "completed_and_confirmed"].includes(this.reservation.status);
            if (!statusOk) return false;
            const ownerCanRate = this.isEntryOwner && this.reservation.completed_confirmed_by_owner;
            const reserverCanRate = !this.isEntryOwner && this.reservation.completed_confirmed_by_reserver;
            return ownerCanRate || reserverCanRate;
        },
        ratingTooltipSubmit() {
            return this.ratingWindowTooltipText(false);
        },
        ratingTooltipEdit() {
            return this.ratingWindowTooltipText(true);
        },
        showEditRating() {
            return (
                this.reservation.user_has_rated &&
                this.reservation.existing_rating?.can_edit &&
                this.reservation.rated_company_id
            );
        },
        showTruckCapacityHint() {
            return (
                this.isEntryOwner &&
                this.entryType === "truck" &&
                this.reservation.has_transport_order &&
                !!this.transportOrderPreview?.confirmed_at
            );
        },
        hasAnyCardActions() {
            return (
                this.ownerPendingCounterOnly ||
                this.canConfirmReservation ||
                this.showReserverPriceOfferControls ||
                this.canAcceptCounterOffer ||
                this.canCancel ||
                this.canConfirmTransportOrder ||
                this.canConfirmCompleted ||
                this.showRatingForm ||
                this.showEditRating
            );
        },
        showTransportOrderIcons() {
            return this.showTransportOrderIcon || this.showSignedPdfIcon || this.showCmrInvoiceSection;
        },
        showCmrInvoiceSection() {
            if (!this.reservation.has_transport_order || !this.transportOrderPreview) return false;
            if (this.isReservationDeclined) return false;
            return ["confirmed", "in_progress", "completed_and_awaiting_confirmation", "completed_and_confirmed"].includes(
                this.reservation.status
            );
        },
        canUploadCmrInvoice() {
            if (!this.showCmrInvoiceSection) return false;
            if (typeof this.reservation.cmr_invoice_upload_allowed === "boolean") {
                return this.reservation.cmr_invoice_upload_allowed;
            }
            return this.canUploadSignedPdf && this.reservation.status === "confirmed";
        },
        cmrSubtitle() {
            if (this.transportOrderPreview?.has_cmr_pdf) {
                const date = this.transportOrderPreview?.cmr_uploaded_at;
                return date ? formatDisplayDateTime(date) : "";
            }
            return this.$t("reservations.noFilePresent");
        },
        cmrSubtitleDate() {
            if (!this.transportOrderPreview?.has_cmr_pdf) return null;
            const date = this.transportOrderPreview?.cmr_uploaded_at;
            return date ? formatDisplayDate(date) : null;
        },
        cmrSubtitleTime() {
            if (!this.transportOrderPreview?.has_cmr_pdf) return null;
            const date = this.transportOrderPreview?.cmr_uploaded_at;
            return date ? formatDisplayTime(date) : null;
        },
        invoiceSubtitle() {
            if (this.transportOrderPreview?.has_invoice_pdf) {
                const date = this.transportOrderPreview?.invoice_uploaded_at;
                return date ? formatDisplayDateTime(date) : "";
            }
            return this.$t("reservations.noFilePresent");
        },
        invoiceSubtitleDate() {
            if (!this.transportOrderPreview?.has_invoice_pdf) return null;
            const date = this.transportOrderPreview?.invoice_uploaded_at;
            return date ? formatDisplayDate(date) : null;
        },
        invoiceSubtitleTime() {
            if (!this.transportOrderPreview?.has_invoice_pdf) return null;
            const date = this.transportOrderPreview?.invoice_uploaded_at;
            return date ? formatDisplayTime(date) : null;
        },
        cmrCardClickable() {
            if (this.isReservationDeclined) return false;
            if (this.canUploadCmrInvoice) return true;
            return !!this.transportOrderPreview?.cmr_pdf_url;
        },
        invoiceCardClickable() {
            if (this.isReservationDeclined) return false;
            if (this.canUploadCmrInvoice) return true;
            return !!this.transportOrderPreview?.invoice_pdf_url;
        },
        cmrTooltipText() {
            if (!this.showCmrInvoiceSection) return "";
            const company =
                (this.isTruckReservation ? this.reservation.owner_company_name : this.reservation.reserved_by_company?.name) || "—";
            const hasCmr = this.transportOrderPreview?.has_cmr_pdf;
            if (!hasCmr && !this.canUploadCmrInvoice) {
                return this.canUploadSignedPdf
                    ? this.$t("reservations.cmrInvoiceUploadClosed")
                    : this.$t("reservations.waitForCompanyToUploadCmr", { company });
            }
            if (!hasCmr && this.canUploadCmrInvoice) {
                return this.$t("reservations.uploadCmr");
            }
            if (hasCmr && this.canUploadCmrInvoice) {
                return this.$t("reservations.clickCardToUploadNewFile");
            }
            return "";
        },
        invoiceTooltipText() {
            if (!this.showCmrInvoiceSection) return "";
            const company =
                (this.isTruckReservation ? this.reservation.owner_company_name : this.reservation.reserved_by_company?.name) || "—";
            const hasInv = this.transportOrderPreview?.has_invoice_pdf;
            if (!hasInv && !this.canUploadCmrInvoice) {
                return this.canUploadSignedPdf
                    ? this.$t("reservations.cmrInvoiceUploadClosed")
                    : this.$t("reservations.waitForCompanyToUploadInvoice", { company });
            }
            if (!hasInv && this.canUploadCmrInvoice) {
                return this.$t("reservations.uploadInvoice");
            }
            if (hasInv && this.canUploadCmrInvoice) {
                return this.$t("reservations.clickCardToUploadNewFile");
            }
            return "";
        },
        canUploadPaymentProof() {
            if (!this.showCmrInvoiceSection) return false;
            if (typeof this.reservation.payment_proof_upload_allowed === "boolean") {
                return this.reservation.payment_proof_upload_allowed;
            }
            return false;
        },
        paymentProofSubtitle() {
            if (this.transportOrderPreview?.has_payment_proof) {
                const date = this.transportOrderPreview?.payment_proof_uploaded_at;
                return date ? formatDisplayDateTime(date) : "";
            }
            return this.$t("reservations.noFilePresent");
        },
        paymentProofSubtitleDate() {
            if (!this.transportOrderPreview?.has_payment_proof) return null;
            const date = this.transportOrderPreview?.payment_proof_uploaded_at;
            return date ? formatDisplayDate(date) : null;
        },
        paymentProofSubtitleTime() {
            if (!this.transportOrderPreview?.has_payment_proof) return null;
            const date = this.transportOrderPreview?.payment_proof_uploaded_at;
            return date ? formatDisplayTime(date) : null;
        },
        paymentProofCardClickable() {
            if (this.isReservationDeclined) return false;
            if (this.canUploadPaymentProof) return true;
            return !!this.transportOrderPreview?.payment_proof_url;
        },
        paymentProofTooltipText() {
            if (!this.showCmrInvoiceSection) return "";
            const company =
                (this.isTruckReservation
                    ? this.reservation.reserved_by_company?.name
                    : this.reservation.owner_company_name) || "—";
            const hasProof = this.transportOrderPreview?.has_payment_proof;
            if (!hasProof && !this.canUploadPaymentProof) {
                return this.$t("reservations.waitForCompanyToUploadPaymentProof", { company });
            }
            if (!hasProof && this.canUploadPaymentProof) {
                return this.$t("reservations.uploadPaymentProof");
            }
            if (hasProof && this.canUploadPaymentProof) {
                return this.$t("reservations.clickCardToUploadNewFile");
            }
            return "";
        },
        showTransportOrderIcon() {
            const waitingForTo = this.reservation.status === "waiting_for_to";
            const ownerNeedsGenerate = this.isEntryOwner && waitingForTo && !this.isTruckReservation;
            const reserverNeedsGenerate = !this.isEntryOwner && waitingForTo && this.isTruckReservation;
            const ownerWaitsForTo = this.isEntryOwner && waitingForTo && this.isTruckReservation;
            const reserverWaitsForTo = !this.isEntryOwner && waitingForTo && !this.isTruckReservation;
            const hasTransportOrderRow = this.reservation.has_transport_order;
            const showLateTransportOrderSlot =
                !hasTransportOrderRow &&
                this.canGenerateTransportOrder &&
                STATUSES_TRANSPORT_DOCUMENT_POST_CONFIRM_FLOW.includes(this.reservation.status);
            return (
                ownerNeedsGenerate ||
                reserverNeedsGenerate ||
                ownerWaitsForTo ||
                reserverWaitsForTo ||
                hasTransportOrderRow ||
                showLateTransportOrderSlot
            );
        },
        showSignedPdfIcon() {
            const hasTransportOrder = this.reservation.has_transport_order && this.transportOrderPreview;
            const hasSignedPdf = this.transportOrderPreview?.has_signed_pdf;

            // Once signed PDF exists, keep it visible for the whole lifecycle
            if (hasTransportOrder && hasSignedPdf) return true;

            // Show for upload flow (before signed PDF exists)
            if (!STATUSES_TRANSPORT_DOCUMENT_POST_CONFIRM_FLOW.includes(this.reservation.status)) return false;
            const waitingForTo = this.reservation.status === "waiting_for_to";
            const ownerWaitsForTo = this.isEntryOwner && waitingForTo && this.isTruckReservation;
            const reserverWaitsForTo = !this.isEntryOwner && waitingForTo && !this.isTruckReservation;
            return hasTransportOrder || ownerWaitsForTo || reserverWaitsForTo;
        },
        hasTransportOrder() {
            return !!this.reservation.has_transport_order;
        },
        transportOrderSubtitle() {
            if (this.hasTransportOrder && this.transportOrderPreview?.generated_at) {
                return formatDisplayDateTime(this.transportOrderPreview.generated_at);
            }
            return this.$t("reservations.noFilePresent");
        },
        transportOrderSubtitleDate() {
            if (this.hasTransportOrder && this.transportOrderPreview?.generated_at) {
                return formatDisplayDate(this.transportOrderPreview.generated_at);
            }
            return null;
        },
        transportOrderSubtitleTime() {
            if (this.hasTransportOrder && this.transportOrderPreview?.generated_at) {
                return formatDisplayTime(this.transportOrderPreview.generated_at);
            }
            return null;
        },
        signedPdfSubtitle() {
            if (this.transportOrderPreview?.confirmed_at || this.transportOrderPreview?.has_signed_pdf) {
                const date = this.transportOrderPreview?.signed_uploaded_at;
                return date ? formatDisplayDateTime(date) : "";
            }
            return this.$t("reservations.noFilePresent");
        },
        signedPdfSubtitleDate() {
            if (!(this.transportOrderPreview?.confirmed_at || this.transportOrderPreview?.has_signed_pdf)) return null;
            const date = this.transportOrderPreview?.signed_uploaded_at;
            return date ? formatDisplayDate(date) : null;
        },
        signedPdfSubtitleTime() {
            if (!(this.transportOrderPreview?.confirmed_at || this.transportOrderPreview?.has_signed_pdf)) return null;
            const date = this.transportOrderPreview?.signed_uploaded_at;
            return date ? formatDisplayTime(date) : null;
        },
        entrySearchUrl() {
            const identificationId = this.reservation.entry_summary?.identification_id;
            if (!identificationId) return null;
            const completed = ["completed_and_awaiting_confirmation", "completed_and_confirmed"].includes(this.reservation.status);
            if (completed) return null;
            const path = this.entryType === "truck" ? "/dashboard/search-trucks" : "/dashboard/search-wares";
            const query = { identificationId };
            if (this.reservation.entry_summary?.show_on_reserved_list) {
                query.showReservedWareOnly = "1";
            }
            return { path, query };
        },
        isReservationDeclined() {
            return this.reservation.status === "declined";
        },
        transportOrderViewOnly() {
            return this.isReservationDeclined;
        },
        signedPdfViewOnly() {
            return this.isReservationDeclined;
        },
        canGenerateTransportOrder() {
            return (this.isTruckReservation ? !this.isEntryOwner : this.isEntryOwner) && !this.isReservationDeclined;
        },
        showTransportOrderMenu() {
            return this.canGenerateTransportOrder;
        },
        transportOrderIsExternalPdf() {
            return this.transportOrderPreview?.transport_order_source === "external_pdf";
        },
        canUploadSignedPdf() {
            return (this.isTruckReservation ? this.isEntryOwner : !this.isEntryOwner) && !this.isReservationDeclined;
        },
        transportOrderNoFileTooltip() {
            if (this.canGenerateTransportOrder) return this.$t("reservations.generateTransportOrder");
            const company = this.isTruckReservation ? this.reservation.reserved_by_company?.name : this.reservation.owner_company_name;
            return this.$t("reservations.waitForCompanyToGenerateTransportOrder", { company: company || "—" });
        },
        signedPdfConfirmedTooltip() {
            if (!this.transportOrderPreview?.confirmed_at) return "";
            const isConfirmingParty = this.isTruckReservation ? !this.isEntryOwner : this.isEntryOwner;
            if (isConfirmingParty) return this.$t("reservations.youConfirmedSignedTransportOrderTooltip");
            const companyName = this.transportOrderPreview?.confirmed_by_company_name || "-";
            return this.$t("reservations.uploadSignedPdfDisabledTooltip", { companyName });
        },
        signedPdfNoFileTooltip() {
            const uploaderCompany = this.isTruckReservation ? this.reservation.owner_company_name : this.reservation.reserved_by_company?.name;
            const company = uploaderCompany || "—";
            if (!this.hasTransportOrder) {
                return this.$t("reservations.signedPdfNeedsUploadByCompany", { company });
            }
            if (!this.canUploadSignedPdf) {
                return this.$t("reservations.waitForCompanyToUploadSignedPdf", { company });
            }
            return this.$t("reservations.uploadSignedPdf");
        },
        signedPdfUploadReplacementTooltip() {
            if (!this.transportOrderPreview?.has_signed_pdf || this.transportOrderPreview?.confirmed_at) return "";
            if (!this.canUploadSignedPdf) return "";
            return this.$t("reservations.clickCardToUploadNewFile");
        },
    },
    methods: {
        routeLinesForSide(role) {
            const summary = this.reservation?.entry_summary;
            if (!summary) return [];
            const stored = this.reservation?.stop_schedules;
            const stops = role === "loading" ? summary.loading_stops : summary.unloading_stops;
            if (Array.isArray(stored) && stored.length && Array.isArray(stops) && stops.length) {
                return stops.map((stop) => this.formatStopRouteLine(stop)).filter(Boolean);
            }
            const line = this.legacyRouteLine(role);
            return line ? [line] : [];
        },
        formatStopRouteLine(stop) {
            const base = stop.summary || [stop.city, stop.country_name].filter(Boolean).join(", ");
            const timePart =
                stop.scheduled_date && stop.scheduled_time
                    ? `${stop.scheduled_date}, ${stop.scheduled_time}`
                    : stop.scheduled_date || stop.scheduled_time || "";
            return base && timePart ? `${base} | ${timePart}` : base || timePart || "";
        },
        legacyRouteLine(role) {
            const entrySummary = this.reservation?.entry_summary;
            if (!entrySummary) return "";
            const base = role === "loading" ? entrySummary.loading_summary : entrySummary.unloading_summary;
            const date = role === "loading" ? entrySummary.loading_scheduled_date : entrySummary.unloading_scheduled_date;
            const time = role === "loading" ? entrySummary.loading_scheduled_time : entrySummary.unloading_scheduled_time;
            const timePart = date && time ? `${date}, ${time}` : date || time || "";
            return base && timePart ? `${base} | ${timePart}` : base || timePart || "";
        },
        /**
         * @param {boolean} forEditTooltip - true = edit rating, false = first-time submit
         */
        ratingWindowTooltipText(forEditTooltip) {
            const remaining = getRatingWindowRemainingParts(this.reservation.rating_window_expires_at);
            if (remaining === null) {
                return "";
            }
            if (remaining.expired) {
                return this.$t("rating.tooltipWindowExpired");
            }
            const timeLabel = this.$t("rating.tooltipTimeFormat", {
                hours: remaining.hours,
                minutes: remaining.minutes,
            });
            if (forEditTooltip) {
                return this.$t("rating.tooltipEditRating", { time: timeLabel });
            }
            return this.$t("rating.tooltipSubmitRating", { time: timeLabel });
        },
        formatNegotiationLine(amount, terms) {
            if (amount == null) return "—";
            if (!terms?.currency) return String(amount);
            const vatLabel =
                terms.vatType === true
                    ? this.$t("formFields.withVAT")
                    : terms.vatType === false
                      ? this.$t("formFields.withoutVAT")
                      : "";
            return `${amount} ${terms.currency} ${vatLabel}`.trim();
        },
        onCounterOfferModelValueUpdate(nextValue) {
            this.counterOfferValue = cleanPriceInput(nextValue);
        },
        onCounterOfferButtonClick() {
            if (!this.showCounterOfferInput) {
                this.showCounterOfferInput = true;
                return;
            }
            if (this.counterOfferValue == null || this.counterOfferValue <= 0) return;
            if (this.isTruckReservation) {
                this.pendingTruckCounterAmount = this.counterOfferValue;
                this.prefillTruckModalFromReservation();
                this.truckOwnerModalMode = "counter";
                this.truckOwnerModalVisible = true;
                return;
            }
            this.$emit("price-counter-offer", { price_counter_offer: this.counterOfferValue });
        },
        onNewOfferModelValueUpdate(nextValue) {
            this.newOfferValue = cleanPriceInput(nextValue);
        },
        onNewOfferButtonClick() {
            if (!this.showNewOfferInput) {
                this.showNewOfferInput = true;
                return;
            }
            if (this.newOfferValue == null || this.newOfferValue <= 0) return;
            if (!this.entryHasPrice) {
                const currency =
                    this.reservation.last_offer_currency || this.reservation.price_offer_currency;
                const vatRaw =
                    this.reservation.last_offer_vat_type ?? this.reservation.price_offer_vat_type;
                if (!currency || (vatRaw !== true && vatRaw !== false)) {
                    showAlertModal(
                        this.$store,
                        this.$t("reservations.errors.priceOfferTermsMissing"),
                        "warning",
                        5000
                    );
                    return;
                }
                this.$emit("price-offer", {
                    price_offer: this.newOfferValue,
                    price_offer_currency: currency,
                    price_offer_vat_type: vatRaw === true,
                });
                return;
            }
            this.$emit("price-offer", { price_offer: this.newOfferValue });
        },
        onTruckModalDriverUpdate(nextDriverFields) {
            this.truckModalDriver = nextDriverFields;
        },
        prefillTruckModalFromReservation() {
            const reservation = this.reservation;
            this.truckModalDriver = {
                driver_name: (reservation.driver_name || "").trim(),
                driver_surname: (reservation.driver_surname || "").trim(),
                car_number: (reservation.car_number || "").trim(),
                customs: (reservation.customs || "").trim(),
            };
        },
        submitTruckOwnerModal() {
            const driverFields = this.truckModalDriver;
            const driverFirstName = (driverFields.driver_name || "").trim();
            const driverLastName = (driverFields.driver_surname || "").trim();
            const vehiclePlate = (driverFields.car_number || "").trim();
            if (!driverFirstName || !driverLastName || !vehiclePlate) {
                showAlertModal(this.$store, this.$t("reservations.truckConfirmDriverRequiredTooltip"), "warning", 5000);
                return;
            }
            const driverPayload = {
                driver_name: driverFirstName,
                driver_surname: driverLastName,
                car_number: vehiclePlate,
                customs: (driverFields.customs || "").trim(),
            };
            if (this.truckOwnerModalMode === "confirm") {
                this.$emit("confirm", driverPayload);
            } else {
                this.$emit("price-counter-offer", {
                    price_counter_offer: this.pendingTruckCounterAmount,
                    ...driverPayload,
                });
            }
            this.truckOwnerModalVisible = false;
        },
        onConfirmClick() {
            if (this.isTruckReservation) {
                this.prefillTruckModalFromReservation();
                this.truckOwnerModalMode = "confirm";
                this.truckOwnerModalVisible = true;
                return;
            }
            this.$emit("confirm", undefined);
        },
        openWhatsApp(url) {
            if (url) window.open(url, "_blank", "noopener,noreferrer");
        },
        openPdf(url) {
            if (url) window.open(url, "_blank", "noopener,noreferrer");
        },
        reservationFileWithinLimit(file) {
            if (!file) return false;
            if (file.size > RESERVATION_DOCUMENT_MAX_BYTES) {
                showAlertModal(this.$store, this.$t("reservations.fileTooLarge"), "danger", 5000);
                return false;
            }
            return true;
        },
        triggerExternalGeneratedPdfUpload() {
            this.$refs.externalGeneratedPdfInput?.click();
        },
        onExternalGeneratedPdfSelected(event) {
            const file = event.target?.files?.[0];
            if (file && this.reservationFileWithinLimit(file)) this.$emit("uploadGeneratedTransportOrder", file);
            event.target.value = "";
        },
        onSignedPdfCardClick() {
            if (this.isReservationDeclined) return;
            if (this.transportOrderPreview?.has_signed_pdf) {
                // Has signed PDF, not yet confirmed - uploader can replace, others can view
                if (this.canUploadSignedPdf) {
                    this.$refs.signedPdfInput?.click();
                } else if (this.transportOrderPreview?.signed_pdf_url) {
                    this.openPdf(this.transportOrderPreview.signed_pdf_url);
                }
            } else {
                // No signed PDF yet - carrier uploads (reserver for ware, entry owner for truck)
                if (!this.hasTransportOrder || !this.canUploadSignedPdf) return;
                this.$refs.signedPdfInput?.click();
            }
        },
        onQuickRatingSelected(value) {
            if (value >= 1 && value <= 5) {
                this.ratingModalVisible = true;
            }
        },
        onRatingSubmitted() {
            this.ratingModalVisible = false;
            this.quickRatingStars = 0;
            this.$emit("rated");
        },
        onRatingCancel() {
            this.ratingModalVisible = false;
            if (this.showRatingForm) {
                this.quickRatingStars = 0;
            }
        },
        onTransportOrderCardClick() {
            if (this.hasTransportOrder && this.transportOrderPreview?.generated_pdf_url) {
                this.openPdf(this.transportOrderPreview.generated_pdf_url);
            } else if (!this.hasTransportOrder) {
                this.$emit("generateTransportOrder");
            }
        },
        onSignedPdfSelected(event) {
            const file = event.target?.files?.[0];
            if (file && this.reservationFileWithinLimit(file)) this.$emit("uploadSignedPdf", file);
            event.target.value = "";
        },
        onCmrCardClick() {
            if (this.isReservationDeclined) return;
            if (this.canUploadCmrInvoice) {
                this.$refs.cmrPdfInput?.click();
                return;
            }
            if (this.transportOrderPreview?.cmr_pdf_url) {
                this.openPdf(this.transportOrderPreview.cmr_pdf_url);
            }
        },
        onInvoiceCardClick() {
            if (this.isReservationDeclined) return;
            if (this.canUploadCmrInvoice) {
                this.$refs.invoicePdfInput?.click();
                return;
            }
            if (this.transportOrderPreview?.invoice_pdf_url) {
                this.openPdf(this.transportOrderPreview.invoice_pdf_url);
            }
        },
        onCmrPdfSelected(event) {
            const file = event.target?.files?.[0];
            if (file && this.reservationFileWithinLimit(file)) this.$emit("uploadCmr", file);
            event.target.value = "";
        },
        onInvoicePdfSelected(event) {
            const file = event.target?.files?.[0];
            if (file && this.reservationFileWithinLimit(file)) this.$emit("uploadInvoice", file);
            event.target.value = "";
        },
        onPaymentProofCardClick() {
            if (this.isReservationDeclined) return;
            if (this.canUploadPaymentProof) {
                this.$refs.paymentProofInput?.click();
                return;
            }
            if (this.transportOrderPreview?.payment_proof_url) {
                this.openPdf(this.transportOrderPreview.payment_proof_url);
            }
        },
        onPaymentProofSelected(event) {
            const file = event.target?.files?.[0];
            if (file && this.reservationFileWithinLimit(file)) this.$emit("uploadPaymentProof", file);
            event.target.value = "";
        },
    },
};
</script>

<style scoped>
.reservation-card-contact-stack {
    min-width: 0;
}

@media (min-width: 600px) {
    .reservation-card-main {
        flex: 1 1 0%;
        width: auto;
        max-width: 100%;
    }

    .reservation-card-contact-stack {
        width: max-content;
        max-width: 100%;
    }
}

.reservation-card-content {
    font-size: 1rem;
}

.entry-id-link {
    color: #FFEB3B;
    text-decoration: none;
}
.entry-id-link:hover {
    color: inherit;
    text-decoration: none;
}

.cursor-pointer {
    cursor: pointer;
}

.reservation-route-line {
    line-height: 1.5;
}

.reservation-route-icon {
    width: 60px;
    height: 18px;
    vertical-align: top;
}

.toolbar-item-separator {
    display: inline-block;
    height: 32px;
    margin: 0 8px;
    border-left: 2px solid #FFEB3B;
    vertical-align: middle;
}
</style>

